/**
 * Reusable occupant form fields with autocomplete + grouped date-of-birth.
 *
 * Components exported:
 *   - CountryCombo     : ISO-3 country code with Italian name autocomplete (datalist)
 *   - ComuneCombo      : Italian municipality autocomplete (lazy-loaded JSON from /data/comuni.json)
 *   - DobSelect        : Day / Month / Year cascade selects (ISO YYYY-MM-DD output)
 *
 * All components are controlled (value/onChange) and intentionally lightweight:
 * we leverage the native <datalist> for great mobile UX, fast filtering, and
 * accessibility without pulling extra dependencies.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "../ui/input";
import { COUNTRIES, COUNTRY_NAME, COUNTRY_CODE_BY_NAME } from "../../data/countries";

// ── COUNTRY COMBO ─────────────────────────────────────────────────────────
// User types/sees an Italian country name. We persist the ISO-3 code in `value`.
export function CountryCombo({ value, onChange, placeholder = "Italia", testId, listId }) {
  const autoId = React.useId();
  // Display the Italian name for the current ISO3 code (or empty)
  const display = value ? (COUNTRY_NAME[value.toUpperCase()] || value) : "";
  const datalistId = listId || `country-list-${autoId}`;

  const handleInput = (e) => {
    const raw = e.target.value;
    if (!raw) return onChange("");
    const code = COUNTRY_CODE_BY_NAME[raw.toLowerCase()];
    if (code) {
      onChange(code);
    } else if (raw.length === 3 && COUNTRY_NAME[raw.toUpperCase()]) {
      // user typed an ISO3 code directly
      onChange(raw.toUpperCase());
    } else {
      // Free-text fallback: store uppercased input (max 3 letters) so backend
      // still receives an ISO-like value. The user will see it as a string.
      onChange(raw.trim());
    }
  };

  return (
    <>
      <Input
        list={datalistId}
        value={display}
        onChange={handleInput}
        placeholder={placeholder}
        className="h-10 text-sm"
        data-testid={testId}
        autoComplete="off"
      />
      <datalist id={datalistId}>
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.name} />
        ))}
      </datalist>
    </>
  );
}

// ── COMUNE COMBO (Italian municipalities) ─────────────────────────────────
// Lazy-load comuni.json once globally, then reuse.
let _comuniCache = null;
let _comuniPromise = null;

function loadComuni() {
  if (_comuniCache) return Promise.resolve(_comuniCache);
  if (_comuniPromise) return _comuniPromise;
  _comuniPromise = fetch("/data/comuni.json")
    .then((r) => r.json())
    .then((d) => {
      _comuniCache = d;
      return d;
    })
    .catch(() => {
      _comuniCache = [];
      return [];
    });
  return _comuniPromise;
}

export function ComuneCombo({ value, onChange, onProvinceAutofill, onIstatAutofill, testId, listId }) {
  const autoId = React.useId();
  const [comuni, setComuni] = useState(_comuniCache || []);
  const datalistId = listId || `comuni-list-${autoId}`;
  const lastValueRef = useRef("");

  useEffect(() => {
    if (!_comuniCache) loadComuni().then(setComuni);
  }, []);

  const handleInput = (e) => {
    const raw = e.target.value;
    onChange(raw);
    lastValueRef.current = raw;
    // When the user picks (or types) an exact match, auto-fill the province + Polizia code
    if (raw) {
      const match = comuni.find((c) => c.n.toLowerCase() === raw.toLowerCase());
      if (match) {
        if (onProvinceAutofill) onProvinceAutofill(match.s);
        if (onIstatAutofill) onIstatAutofill(match.p || "");
      } else if (onIstatAutofill) {
        // Clear stale code if the user is editing past a previous selection
        onIstatAutofill("");
      }
    } else if (onIstatAutofill) {
      onIstatAutofill("");
    }
  };

  // Limit datalist size — most browsers struggle past ~2000 entries.
  // Show top matches that start with current input, fallback to popular ones.
  const filtered = useMemo(() => {
    if (!comuni.length) return [];
    const q = (value || "").trim().toLowerCase();
    if (!q) return comuni.slice(0, 200); // first slice when empty
    const starts = [];
    const contains = [];
    for (const c of comuni) {
      const n = c.n.toLowerCase();
      if (n.startsWith(q)) starts.push(c);
      else if (n.includes(q)) contains.push(c);
      if (starts.length >= 50) break;
    }
    return [...starts, ...contains].slice(0, 60);
  }, [comuni, value]);

  return (
    <>
      <Input
        list={datalistId}
        value={value || ""}
        onChange={handleInput}
        placeholder="Es. Reggio Calabria"
        className="h-10 text-sm"
        data-testid={testId}
        autoComplete="off"
      />
      <datalist id={datalistId}>
        {filtered.map((c) => (
          <option key={c.c} value={c.n}>{c.s}</option>
        ))}
      </datalist>
    </>
  );
}

// ── DOB SELECT (Giorno / Mese / Anno) ─────────────────────────────────────
const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

function daysInMonth(year, month) {
  // month is 1-12; new Date(year, month, 0) returns last day of `month`
  if (!year || !month) return 31;
  return new Date(Number(year), Number(month), 0).getDate();
}

export function DobSelect({ value, onChange, testIdPrefix }) {
  // value is ISO "YYYY-MM-DD" or empty.
  // The previous implementation derived day/month/year directly from
  // `value` and called `onChange("")` whenever ANY of the three parts
  // was still empty. That meant the very first dropdown you touched
  // (say "Giorno = 1") would emit an empty string upstream, the parent
  // would set value="" again, the select would re-render with no
  // selection and the user couldn't make any progress — the date of
  // birth would "block" exactly as the host reported.
  //
  // Fix: keep a local (y, m, d) state that retains partial selections.
  // We only call `onChange` with an ISO string when ALL three parts are
  // present, AND we always re-sync from `value` when the parent changes
  // it (e.g. when a previously-saved DOB is loaded from the API).
  const parse = (v) => {
    if (!v) return ["", "", ""];
    const [yy, mm, dd] = v.split("-");
    return [yy || "", mm || "", dd || ""];
  };
  const [parts, setParts] = useState(() => parse(value));
  // Resync local state when the prop changes externally (e.g. the
  // form loads a previously-saved DOB). We use useEffect so that
  // partial selections the user is making aren't clobbered by the
  // every-render re-derivation that bit the previous implementation.
  // eslint-disable-next-line
  useEffect(() => { setParts(parse(value)); }, [value]);
  const [y, m, d] = parts;

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const list = [];
    for (let i = currentYear; i >= currentYear - 110; i--) list.push(i);
    return list;
  }, [currentYear]);

  const commit = (ny, nm, nd) => {
    // Clamp day to the actual length of the chosen month/year (so
    // 31 February rolls down to 28/29 silently rather than producing
    // an invalid ISO date that the backend would reject).
    let dd = nd;
    if (ny && nm && nd) {
      const max = daysInMonth(ny, nm);
      dd = String(Math.min(Number(nd), max)).padStart(2, "0");
    }
    setParts([ny, nm, dd]);
    if (ny && nm && dd) {
      onChange(`${ny}-${String(nm).padStart(2, "0")}-${dd}`);
    } else {
      // Partial selection → tell the parent we don't have a value
      // yet, but keep the local state intact so the user's clicks
      // remain visible in the dropdowns.
      onChange("");
    }
  };

  const selectCls =
    "h-10 px-2 text-sm bg-white border border-[#E8E2D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#AC5D45]";

  return (
    <div className="flex gap-1.5">
      <select
        className={`${selectCls} flex-1 min-w-[60px]`}
        value={d || ""}
        onChange={(e) => commit(y, m, e.target.value)}
        data-testid={testIdPrefix ? `${testIdPrefix}-day` : undefined}
        aria-label="Giorno"
      >
        <option value="">GG</option>
        {Array.from({ length: daysInMonth(y, m) }, (_, i) => i + 1).map((n) => (
          <option key={n} value={String(n).padStart(2, "0")}>{String(n).padStart(2, "0")}</option>
        ))}
      </select>
      <select
        className={`${selectCls} flex-[1.4] min-w-[90px]`}
        value={m || ""}
        onChange={(e) => commit(y, e.target.value, d)}
        data-testid={testIdPrefix ? `${testIdPrefix}-month` : undefined}
        aria-label="Mese"
      >
        <option value="">Mese</option>
        {MONTHS_IT.map((name, i) => (
          <option key={name} value={String(i + 1).padStart(2, "0")}>{name}</option>
        ))}
      </select>
      <select
        className={`${selectCls} flex-1 min-w-[78px]`}
        value={y || ""}
        onChange={(e) => commit(e.target.value, m, d)}
        data-testid={testIdPrefix ? `${testIdPrefix}-year` : undefined}
        aria-label="Anno"
      >
        <option value="">Anno</option>
        {years.map((yr) => (
          <option key={yr} value={String(yr)}>{yr}</option>
        ))}
      </select>
    </div>
  );
}
